import { queryOptions, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { QUERY_KEYS } from '~shared/api/query-keys';
import type { Days } from '~shared/constants/days';

import type { EventDetails } from '..';

export interface GetAllEventsParams {
  limit?: number;
  page?: number;

  age?: string | number;
  days?: Days;
  instructor?: string;
  name?: string;
  room?: string;
  tags?: string;
  session?: string;
  sessionId?: string;
  isStudio?: boolean;
  isArchive?: boolean;
}

export interface GetAllEventsResponse {
  data: {
    events: Array<EventDetails>;
    rooms: Array<{
      _id: string;
      name: string;
      fieldType: 'room';
    }>;
    instructors: Array<{
      _id: string;
      name: string;
      fieldType: 'instructor';
    }>;
    tags: Array<{
      _id: string;
      name: string;
      fieldType: 'tags';
    }>;
    sessions: Array<{
      _id: string;
      name: string;
    }>;
  };
  pagination: Pagination;
}

export interface Pagination {
  page: number;
  limit: number;
  totalCount: number;
  totalPages: number;
}

export const getAllEventsWithSearchAPI = async (
  params?: GetAllEventsParams,
) => {
  const response = await baseAPI.get<GetAllEventsResponse>(
    `events/search/event`,
    {
      params,
    },
  );
  return response.data;
};

export const getAllEventsWithSearchOptions = (params?: GetAllEventsParams) => {
  return queryOptions({
    queryKey: QUERY_KEYS.EVENTS(params),
    queryFn: () => getAllEventsWithSearchAPI(params),
    staleTime: 1000 * 60 * 5,
  });
};

export const useGetAllEventsWithSearch = (params?: GetAllEventsParams) => {
  return useQuery(getAllEventsWithSearchOptions(params));
};

const DEFAULT_INFINITE_PAGE_SIZE = 30;

type GetAllEventsInfiniteParams = Omit<GetAllEventsParams, 'page'>;

export const useGetAllEventsWithSearchInfinite = (
  params: GetAllEventsInfiniteParams = {},
) => {
  const limit = params.limit ?? DEFAULT_INFINITE_PAGE_SIZE;
  const paramsForKey: GetAllEventsParams = { ...params, limit, page: undefined };

  return useInfiniteQuery({
    queryKey: [...QUERY_KEYS.EVENTS(paramsForKey), 'infinite'] as const,
    queryFn: ({ pageParam }) =>
      getAllEventsWithSearchAPI({
        ...params,
        limit,
        page: pageParam,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      if (totalPages === 0 || page >= totalPages) return undefined;
      return page + 1;
    },
    staleTime: 1000 * 60 * 5,
  });
};
